import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import RatingModal from '@/components/RatingModal';

const SKIP_KEY = 'gambgo:skippedRatings';

const getSkipped = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(SKIP_KEY) || '[]');
  } catch {
    return [];
  }
};

const addSkipped = (orderId: string) => {
  const list = getSkipped();
  if (!list.includes(orderId)) {
    list.push(orderId);
    // Keep the list bounded
    const trimmed = list.slice(-200);
    localStorage.setItem(SKIP_KEY, JSON.stringify(trimmed));
  }
};

interface PendingOrder {
  id: string;
  restaurant_id: string;
  driver_id: string | null;
  restaurants: { name: string; image_url: string | null } | null;
  order_items: { quantity: number; name: string | null; menu_items: { name: string } | null }[];
}

const PendingRatingPrompt = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [order, setOrder] = useState<PendingOrder | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setOrder(null);
      setOpen(false);
      return;
    }
    // Don't pop on order history page (they can rate from there)
    if (location.pathname.startsWith('/order-history')) return;
    // Avoid auth/checkout/tracking interruptions
    const blocked = ['/auth', '/checkout', '/cart', '/ride/track', '/splash'];
    if (blocked.some((p) => location.pathname.startsWith(p))) return;

    let cancelled = false;

    const check = async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `id, restaurant_id, driver_id, status, delivery_type, completed_at,
           restaurants ( name, image_url ),
           order_items ( quantity, name, menu_items ( name ) ),
           ratings ( id )`
        )
        .eq('customer_id', user.id)
        .in('status', ['delivered', 'picked_up'])
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(10);

      if (cancelled || error || !data) return;

      const skipped = getSkipped();
      const candidate = data.find(
        (o: any) =>
          (!o.ratings || o.ratings.length === 0) &&
          !skipped.includes(o.id) &&
          (o.status === 'delivered' ||
            (o.status === 'picked_up' && o.delivery_type === 'pickup'))
      );

      if (candidate) {
        setOrder(candidate as any);
        setOpen(true);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [user, location.pathname]);

  if (!user || !order) return null;

  const items = (order.order_items || []).map((i) => ({
    name: i.name || i.menu_items?.name || 'Item',
    quantity: i.quantity,
  }));

  const handleClose = () => {
    // Treat any close (skip / X / submit) as "don't show this order again"
    addSkipped(order.id);
    setOpen(false);
    setOrder(null);
  };

  return (
    <RatingModal
      isOpen={open}
      onClose={handleClose}
      orderId={order.id}
      restaurantId={order.restaurant_id}
      driverId={order.driver_id || ''}
      customerId={user.id}
      restaurantName={order.restaurants?.name}
      restaurantImage={order.restaurants?.image_url}
      items={items}
    />
  );
};

export default PendingRatingPrompt;
